import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, refreshPushSubscription } from '@/lib/auth';
import { api, Order } from '@/lib/api';
import { Package, Clock, Truck, LogOut, CheckCircle2, Bike, Wallet } from 'lucide-react';
import DriverEarnings from '@/pages/driver/Earnings';

export default function DriverHome() {
  const { session, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'new' | 'active' | 'earnings'>('new');
  const [orders, setOrders] = useState<Order[]>([]);

  useEffect(() => {
    if (!authLoading && (!session || session.role !== 'driver')) {
      navigate('/driver/login', { replace: true });
    }
  }, [session, authLoading, navigate]);

  useEffect(() => {
    if (session?.role === 'driver') {
      refreshPushSubscription(session.token);
    }
  }, [session]);

  const fetchOrders = async () => {
    if (!session || session.role !== 'driver') return;
    try {
      const data = await api.orders.list(session.token);
      setOrders(data.orders);
    } catch {}
  };

  useEffect(() => {
    if (!session || session.role !== 'driver') return;
    fetchOrders();
    const i = setInterval(fetchOrders, 5000);
    return () => clearInterval(i);
  }, [session]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center" dir="rtl">
        <Bike className="w-10 h-10 text-cyan-400 animate-pulse" />
      </div>
    );
  }

  if (!session || session.role !== 'driver') return null;

  const newOrders = orders.filter(o => o.status === 'pending');
  const activeOrders = orders.filter(o => o.status === 'accepted' || o.status === 'in_delivery');

  const handleLogout = () => {
    logout();
    navigate('/driver/login', { replace: true });
  };

  const handleAccept = async (orderId: number) => {
    try {
      await api.orders.updateStatus(orderId, 'accepted', session.token);
      fetchOrders();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleReject = async (orderId: number) => {
    try {
      await api.orders.updateStatus(orderId, 'rejected', session.token);
      fetchOrders();
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-navy-950" dir="rtl">
      <header className="bg-navy-900/90 backdrop-blur-lg border-b border-navy-800 sticky top-0 z-50">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-cyan-700/20 rounded-xl flex items-center justify-center">
              <Bike className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <p className="text-white font-bold text-sm">{session.display_name}</p>
              <p className="text-navy-400 text-xs">سائق توصيل</p>
            </div>
          </div>
          <button onClick={handleLogout} className="text-navy-400 hover:text-red-400 transition-colors p-2">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="px-4 py-4">
        <div className="flex gap-2 mb-5">
          <TabButton
            active={tab === 'new'}
            onClick={() => setTab('new')}
            icon={<Clock className="w-4 h-4" />}
            label="جديدة"
            badge={newOrders.length > 0 ? newOrders.length : undefined}
            badgeColor="bg-red-500 text-white"
          />
          <TabButton
            active={tab === 'active'}
            onClick={() => setTab('active')}
            icon={<Truck className="w-4 h-4" />}
            label="نشطة"
            badge={activeOrders.length > 0 ? activeOrders.length : undefined}
            badgeColor="bg-cyan-400 text-navy-900"
          />
          <TabButton
            active={tab === 'earnings'}
            onClick={() => setTab('earnings')}
            icon={<Wallet className="w-4 h-4" />}
            label="أرباحي"
          />
        </div>

        {tab === 'new' && (
          <div className="space-y-3 animate-fade-in">
            {newOrders.length === 0 ? (
              <EmptyState icon={<Clock className="w-14 h-14" />} message="لا توجد طلبات جديدة حالياً" />
            ) : (
              newOrders.map(order => (
                <OrderCard key={order.id} order={order} onOpen={() => navigate(`/driver/order/${order.id}`)}>
                  <div className="flex gap-2 mt-3 pt-3 border-t border-navy-700">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAccept(order.id); }}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-1"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      قبول
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleReject(order.id); }}
                      className="flex-1 bg-navy-700 hover:bg-red-600/30 text-navy-300 hover:text-red-400 py-2.5 rounded-xl text-sm font-medium transition-colors"
                    >
                      رفض
                    </button>
                  </div>
                </OrderCard>
              ))
            )}
          </div>
        )}

        {tab === 'active' && (
          <div className="space-y-3 animate-fade-in">
            {activeOrders.length === 0 ? (
              <EmptyState icon={<Package className="w-14 h-14" />} message="لا توجد طلبات نشطة" />
            ) : (
              activeOrders.map(order => (
                <OrderCard key={order.id} order={order} onOpen={() => navigate(`/driver/order/${order.id}`)} />
              ))
            )}
          </div>
        )}

        {tab === 'earnings' && (
          <DriverEarnings orders={orders} />
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon, label, badge, badgeColor }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string; badge?: number; badgeColor?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl font-medium transition-all relative text-sm ${
        active ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-900/30' : 'bg-navy-800 text-navy-300'
      }`}
    >
      {icon}
      {label}
      {badge !== undefined && (
        <span className={`absolute -top-1 -left-1 ${badgeColor} text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold`}>
          {badge}
        </span>
      )}
    </button>
  );
}

function OrderCard({ order, onOpen, children }: { order: Order; onOpen: () => void; children?: React.ReactNode }) {
  const statusLabels: Record<string, string> = {
    pending: 'بانتظارك',
    accepted: 'مقبول - جاهز للتوصيل',
    in_delivery: 'قيد التوصيل',
  };

  const statusColors: Record<string, string> = {
    pending: 'text-amber-400',
    accepted: 'text-blue-400',
    in_delivery: 'text-cyan-400',
  };

  return (
    <div
      onClick={onOpen}
      className="bg-navy-800/60 border border-navy-700 rounded-2xl p-4 active:scale-[0.98] transition-transform cursor-pointer"
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-white font-bold">{order.customer_name}</p>
          <p className={`text-xs mt-0.5 ${statusColors[order.status] || 'text-navy-400'}`}>
            {statusLabels[order.status] || order.status}
          </p>
        </div>
        {order.amount > 0 && (
          <span className="bg-cyan-700/20 text-cyan-400 text-sm font-bold px-3 py-1 rounded-lg">
            {order.amount.toFixed(2)}
          </span>
        )}
      </div>
      <p className="text-navy-300 text-sm truncate">{order.delivery_address}</p>
      {children}
    </div>
  );
}

function EmptyState({ icon, message }: { icon: React.ReactNode; message: string }) {
  return (
    <div className="text-center py-16 text-navy-500">
      <div className="mx-auto mb-4 opacity-40">{icon}</div>
      <p className="text-lg">{message}</p>
      <p className="text-sm mt-1 text-navy-600">سيتم تحديث القائمة تلقائياً</p>
    </div>
  );
}
